// Markov Agents — Private Credit Intelligence
// Deep navy institutional palette with steel blue accents and gold highlights
// Designed for independent quant & investor analysis

export const colors = {
  // Base surfaces (deep navy gradient)
  navyDeep: '#080e1a',       // Deepest background
  navy: '#0d1526',           // Primary background
  navySurface: '#131d33',    // Card/panel surfaces
  navyElevated: '#1a2744',   // Elevated elements (hover, modals)
  navyBright: '#223555',     // Borders, dividers

  // Text hierarchy (light on dark)
  textPrimary: '#e2e8f0',    // Primary text
  textSecondary: '#94a3b8',  // Secondary text
  textMuted: '#64748b',      // Muted/tertiary
  textFaint: '#475569',      // Faintest text, disabled

  // Accent: Steel blue (primary action color)
  steel: '#5090d3',          // Primary accent
  steelLight: '#7bb3e8',     // Hover/highlight
  steelBg: '#5090d310',      // Accent background
  steelBorder: '#5090d330',  // Accent border

  // Accent: Gold (emphasis, premium, key metrics)
  gold: '#d4a843',           // Gold accent
  goldLight: '#e8c76a',      // Light gold
  goldBg: '#d4a84310',       // Gold background
  goldBorder: '#d4a84330',   // Gold border

  // Signal colors
  greenInk: '#4ade80',       // Positive/bullish
  greenBg: '#4ade8010',
  greenBorder: '#4ade8025',

  redInk: '#f87171',         // Negative/bearish
  redBg: '#f8717110',
  redBorder: '#f8717125',

  amberInk: '#fbbf24',       // Warning/neutral
  amberBg: '#fbbf2410',
  amberBorder: '#fbbf2425',

  // Borders & rules
  rule: '#1e2d4a',           // Standard border
  ruleLight: '#162238',      // Subtle border
  ruleHeavy: '#2a3d5c',     // Emphasis border
}

export const fonts = {
  serif: "'Crimson Pro', 'Georgia', serif",
  mono: "'IBM Plex Mono', 'Menlo', monospace",
  sans: "'Inter', -apple-system, sans-serif",
}

export const sentiment = {
  positive: { color: colors.greenInk, bg: colors.greenBg, border: colors.greenBorder },
  neutral: { color: colors.amberInk, bg: colors.amberBg, border: colors.amberBorder },
  negative: { color: colors.redInk, bg: colors.redBg, border: colors.redBorder },
  featured: { color: colors.steel, bg: colors.steelBg, border: colors.steelBorder },
  accent: { color: colors.gold, bg: colors.goldBg, border: colors.goldBorder },
}

export const cssVars = `
  :root {
    --navy-deep: ${colors.navyDeep};
    --navy: ${colors.navy};
    --navy-surface: ${colors.navySurface};
    --navy-elevated: ${colors.navyElevated};
    --navy-bright: ${colors.navyBright};
    --text-primary: ${colors.textPrimary};
    --text-secondary: ${colors.textSecondary};
    --text-muted: ${colors.textMuted};
    --text-faint: ${colors.textFaint};
    --steel: ${colors.steel};
    --steel-light: ${colors.steelLight};
    --gold: ${colors.gold};
    --gold-light: ${colors.goldLight};
    --green: ${colors.greenInk};
    --red: ${colors.redInk};
    --amber: ${colors.amberInk};
    --rule: ${colors.rule};
    --rule-light: ${colors.ruleLight};
    --serif: ${fonts.serif};
    --mono: ${fonts.mono};
    --sans: ${fonts.sans};
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    font-family: var(--sans);
    background: var(--navy);
    color: var(--text-primary);
    font-size: 13px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
  }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--navy-bright); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--text-faint); }
`

// Shared button styles
const btnBase = {
  padding: '4px 8px',
  fontSize: 11,
  fontFamily: fonts.mono,
  fontWeight: 600,
  border: 'none',
  borderRadius: 3,
  cursor: 'pointer',
  lineHeight: 1.4,
}
export const buttons = {
  primary: { ...btnBase, background: colors.steel, color: colors.navyDeep },
  accent: { ...btnBase, background: colors.gold, color: colors.navyDeep },
  danger: { ...btnBase, background: colors.redInk, color: colors.navyDeep },
  ghost: { ...btnBase, background: 'transparent', color: colors.steel, border: `1px solid ${colors.steelBorder}` },
  ghostGold: { ...btnBase, background: 'transparent', color: colors.gold, border: `1px solid ${colors.goldBorder}` },
}

export default {
  colors: {
    ...colors,
    text: colors.textPrimary,
    textSecondary: colors.textSecondary,
    background: colors.navy,
    backgroundAlt: colors.navyDeep,
    surface: colors.navySurface,
    border: colors.rule,
    primary: colors.steel,
    success: colors.greenInk,
    danger: colors.redInk,
    warning: colors.amberInk,
    info: colors.steel,
    neutral: colors.textMuted,
  },
  fonts,
  sentiment,
}
